import React, { useEffect, useMemo, useState } from "react";
import { executeQuery } from "../../../graphql/client";

// Attribution tab (epic #293 phase 2): where new people come from and
// who brings them. Reads the hearAbout / refCode attributes phase 1
// stamps on socialUsers rows via the admin-only signupAttribution
// query. Three questions, top to bottom:
//   1. Which channel fills the funnel (sources, with how far each
//      channel's people get: started → account → approved → paid)
//   2. Is it trending (12-week signups, attributed share)
//   3. Who brings people (referral leaderboard, refCode resolved to
//      the member; unresolved codes are shown as-is so nothing hides)
// Phase 3 (what a referral earns) is David's decision — this tab only
// counts.

const SOURCE_LABELS = {
  instagram: "Instagram",
  facebook: "Facebook",
  friend: "Friend / member",
  google: "Google",
  "walked-up": "Met at an event",
  other: "Other",
  "referral-link": "Referral link (no source picked)",
  unspecified: "Not asked / not answered",
};

const STATUS_LABELS = {
  lead: "lead",
  account: "account",
  approved: "approved",
  paid: "paid 💳",
};

const WINDOWS = [
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
  { days: 365, label: "1 year" },
  { days: 0, label: "All time" },
];

const OCEAN = "var(--ab-ocean, #0e7c9b)";
const SEAFOAM = "var(--ab-seafoam, #e7f4f3)";

const sourceLabel = (s) => SOURCE_LABELS[s] || s;

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const fmtWeek = (ymd) => {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const pct = (n, d) => (d ? `${Math.round((100 * n) / d)}%` : "–");

const pill = (active) => ({
  border: active ? `2px solid ${OCEAN}` : "1px solid #d7dbe4",
  background: active ? SEAFOAM : "#fff",
  color: "#333",
  borderRadius: 999,
  padding: "6px 14px",
  fontSize: 13,
  fontWeight: 700,
  cursor: "pointer",
});

const card = {
  border: "1px solid #e6e9f0",
  borderRadius: 10,
  background: "#fff",
  padding: "12px 14px",
  marginBottom: 14,
};

const muted = { color: "#6a7385", fontSize: 13 };

const th = {
  textAlign: "left",
  fontSize: 12,
  color: "#6a7385",
  fontWeight: 600,
  padding: "4px 6px",
  borderBottom: "1px solid #e6e9f0",
  whiteSpace: "nowrap",
};
const td = { padding: "6px", fontSize: 14, verticalAlign: "middle" };
const tdNum = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" };

// Thin single-hue bar: magnitude only, so one color. Width is relative
// to the largest value in the same table; label stays in text ink.
const Bar = ({ value, max }) => (
  <div
    style={{
      height: 8,
      borderRadius: 4,
      background: "#eef1f5",
      minWidth: 80,
      overflow: "hidden",
    }}
  >
    <div
      style={{
        height: "100%",
        width: max ? `${Math.max(2, (100 * value) / max)}%` : 0,
        background: OCEAN,
        borderRadius: 4,
      }}
    />
  </div>
);

const Stat = ({ label, value, sub }) => (
  <div
    style={{
      ...card,
      marginBottom: 0,
      flex: "1 1 140px",
      padding: "10px 12px",
    }}
  >
    <div style={{ fontSize: 12, color: "#6a7385", fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 800, color: "#1f2330" }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: "#8a93a3" }}>{sub}</div>}
  </div>
);

const AttributionTab = () => {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openRef, setOpenRef] = useState("");

  const load = async (windowDays) => {
    setLoading(true);
    setError("");
    try {
      const out = await executeQuery(
        `query SignupAttribution($days: Int) { signupAttribution(days: $days) }`,
        { days: windowDays }
      );
      setData(out?.signupAttribution ? JSON.parse(out.signupAttribution) : null);
    } catch (err) {
      setError(err.message || "Couldn't load attribution.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const sources = data?.sources || [];
  const weeks = data?.weeks || [];
  const referrers = data?.referrers || [];
  const recent = data?.recent || [];
  const totals = data?.totals || { rows: 0, attributed: 0, referred: 0, paidAttributed: 0 };

  const maxStarted = useMemo(
    () => sources.reduce((m, s) => Math.max(m, s.started), 0),
    [sources]
  );
  const maxWeek = useMemo(
    () => weeks.reduce((m, w) => Math.max(m, w.total), 0),
    [weeks]
  );
  const windowLabel = (WINDOWS.find((w) => w.days === days) || {}).label || "";

  return (
    <div style={{ maxWidth: 820 }}>
      <h2 style={{ marginTop: 0 }}>
        <span role="img" aria-label="compass">🧭</span> Signup Attribution
      </h2>
      <p style={muted}>
        Where new people come from and who brings them. Counts the "How did
        you hear about us?" answer and the <code>?ref=</code> code captured on
        /join and /signup (epic #293). Rows from before that shipped show as
        "not asked", so the attributed share climbs from here.
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        {WINDOWS.map((w) => (
          <button
            key={w.days}
            type="button"
            onClick={() => setDays(w.days)}
            style={pill(days === w.days)}
          >
            {w.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => load(days)}
          disabled={loading}
          style={{ ...pill(false), background: "#f6f7fa", fontWeight: 500 }}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {error && (
        <div
          style={{
            background: "#fdecea",
            border: "1px solid #e7b3ba",
            color: "#b00020",
            borderRadius: 8,
            padding: "10px 14px",
            marginBottom: 12,
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      {data && (
        <>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
            <Stat
              label={`Signups started · ${windowLabel}`}
              value={totals.rows}
              sub="rows with a first-touch date in the window"
            />
            <Stat
              label="Attributed"
              value={pct(totals.attributed, totals.rows)}
              sub={`${totals.attributed} told us a source or used a link`}
            />
            <Stat
              label="Came via a member link"
              value={totals.referred}
              sub={`${referrers.length} referrer${referrers.length === 1 ? "" : "s"}`}
            />
            <Stat
              label="Attributed → paid"
              value={totals.paidAttributed}
              sub="paying members we can credit to a channel"
            />
          </div>

          <div style={card}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>
              Sources · {windowLabel}
            </div>
            <div style={{ ...muted, marginBottom: 8 }}>
              How far each channel's people get. "Started" is anyone who hit
              the form; the three columns to the right are the funnel.
            </div>
            {sources.length === 0 ? (
              <p style={muted}>No signups in this window.</p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={th}>Source</th>
                      <th style={{ ...th, width: "28%" }}></th>
                      <th style={{ ...th, textAlign: "right" }}>Started</th>
                      <th style={{ ...th, textAlign: "right" }}>Account</th>
                      <th style={{ ...th, textAlign: "right" }}>Approved</th>
                      <th style={{ ...th, textAlign: "right" }}>Paid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sources.map((s) => (
                      <tr
                        key={s.source}
                        style={{
                          color: s.source === "unspecified" ? "#8a93a3" : "#1f2330",
                        }}
                      >
                        <td style={td}>{sourceLabel(s.source)}</td>
                        <td style={td}>
                          {s.source !== "unspecified" && (
                            <Bar value={s.started} max={maxStarted} />
                          )}
                        </td>
                        <td style={tdNum}>{s.started}</td>
                        <td style={tdNum}>{s.accounts}</td>
                        <td style={tdNum}>{s.approved}</td>
                        <td style={{ ...tdNum, fontWeight: 700 }}>{s.paid}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div style={card}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>
              Last 12 weeks · all signups
            </div>
            <div style={{ ...muted, marginBottom: 8 }}>
              Weekly signups started (Mon–Sun), with how many carried a source.
              Independent of the window above.
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={th}>Week of</th>
                    <th style={{ ...th, width: "32%" }}></th>
                    <th style={{ ...th, textAlign: "right" }}>Started</th>
                    <th style={{ ...th, textAlign: "right" }}>Attributed</th>
                    <th style={th}>Top sources</th>
                  </tr>
                </thead>
                <tbody>
                  {weeks.map((w) => {
                    const top = Object.entries(w.bySource || {})
                      .filter(([k]) => k !== "unspecified")
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 3)
                      .map(([k, n]) => `${sourceLabel(k)} ${n}`)
                      .join(" · ");
                    return (
                      <tr key={w.weekStart}>
                        <td style={{ ...td, whiteSpace: "nowrap" }}>
                          {fmtWeek(w.weekStart)}
                        </td>
                        <td style={td}>
                          <Bar value={w.total} max={maxWeek} />
                        </td>
                        <td style={tdNum}>{w.total}</td>
                        <td style={tdNum}>
                          {w.total ? `${w.attributed} (${pct(w.attributed, w.total)})` : "–"}
                        </td>
                        <td style={{ ...td, fontSize: 12.5, color: "#5b6b7c" }}>
                          {top || (w.total ? "—" : "")}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div style={card}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>
              Referral leaderboard · {windowLabel}
            </div>
            <div style={{ ...muted, marginBottom: 8 }}>
              Members whose invite link brought people, ranked by paid, then
              accounts, then total. Tap a row to see who they brought. Codes
              that don't match a member yet are listed as-is.
            </div>
            {referrers.length === 0 ? (
              <p style={muted}>
                No one has come through a member link in this window yet. The
                "Bring a friend" card on the dashboard is where links come
                from.
              </p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {referrers.map((r, i) => {
                  const open = openRef === r.refCode;
                  return (
                    <div
                      key={r.refCode}
                      style={{
                        border: "1px solid #e6e9f0",
                        borderRadius: 8,
                        padding: "8px 10px",
                        background: i === 0 ? "#fbfdf7" : "#fff",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => setOpenRef(open ? "" : r.refCode)}
                        style={{
                          all: "unset",
                          display: "flex",
                          width: "100%",
                          alignItems: "center",
                          gap: 10,
                          cursor: "pointer",
                          boxSizing: "border-box",
                        }}
                      >
                        <div style={{ width: 24, fontWeight: 800, color: "#8a93a3" }}>
                          {i + 1}
                        </div>
                        <div style={{ flex: "1 1 160px", minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 14 }}>
                            {r.resolved
                              ? r.referrerName || r.referrerEmail || "Member"
                              : `Unmatched code ${r.refCode}`}
                          </div>
                          <div style={{ fontSize: 12, color: "#8a93a3" }}>
                            {r.resolved && r.referrerEmail ? `${r.referrerEmail} · ` : ""}
                            last {fmtDate(r.latestAt)} · code {r.refCode}
                          </div>
                        </div>
                        <div style={{ ...tdNum, padding: 0, fontSize: 13, whiteSpace: "nowrap" }}>
                          <strong>{r.total}</strong> brought · {r.accounts} acct ·{" "}
                          {r.approved} appr · <strong>{r.paid} paid</strong>
                        </div>
                        <div style={{ color: "#8a93a3" }}>{open ? "▾" : "▸"}</div>
                      </button>
                      {open && (
                        <div
                          style={{
                            marginTop: 8,
                            paddingTop: 8,
                            borderTop: "1px dashed #e6e9f0",
                            display: "flex",
                            flexDirection: "column",
                            gap: 4,
                          }}
                        >
                          {r.referred.map((p) => (
                            <div
                              key={p.userId}
                              style={{ fontSize: 13, display: "flex", gap: 8, flexWrap: "wrap" }}
                            >
                              <span style={{ fontWeight: 600 }}>
                                {p.name || p.email || "(no name)"}
                              </span>
                              {p.name && p.email && (
                                <span style={{ color: "#8a93a3" }}>{p.email}</span>
                              )}
                              <span style={{ color: "#5b6b7c" }}>
                                {fmtDate(p.date)} · {STATUS_LABELS[p.status] || p.status}
                                {p.source !== "referral-link" && p.source !== "unspecified"
                                  ? ` · said ${sourceLabel(p.source)}`
                                  : ""}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div style={card}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>
              Recent attributed signups · {windowLabel}
            </div>
            {recent.length === 0 ? (
              <p style={muted}>Nothing attributed in this window yet.</p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={th}>When</th>
                      <th style={th}>Who</th>
                      <th style={th}>Source</th>
                      <th style={th}>Brought by</th>
                      <th style={th}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recent.map((p) => (
                      <tr key={p.userId}>
                        <td style={{ ...td, whiteSpace: "nowrap", color: "#5b6b7c" }}>
                          {fmtDate(p.date)}
                        </td>
                        <td style={td}>
                          <div style={{ fontWeight: 600 }}>{p.name || "(no name)"}</div>
                          {p.email && (
                            <div style={{ fontSize: 12, color: "#8a93a3", wordBreak: "break-all" }}>
                              {p.email}
                            </div>
                          )}
                        </td>
                        <td style={td}>{sourceLabel(p.source)}</td>
                        <td style={{ ...td, color: "#5b6b7c" }}>
                          {p.refCode
                            ? p.referrerName || `code ${p.refCode}`
                            : "—"}
                        </td>
                        <td style={td}>{STATUS_LABELS[p.status] || p.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div style={{ ...muted, fontSize: 12 }}>
            Generated {new Date(data.generatedAt).toLocaleString()}. First touch
            = earliest date on the row, so legacy rows keep their original
            join date.
          </div>
        </>
      )}
    </div>
  );
};

export default AttributionTab;
